/*The main form editor component*/
import React, { Component } from "react"
import DynamicFormTemplate from "./DynamicFormTemplate/DynamicFormTemplate"
import DynamicFormFieldManager from "./DynamicFormFieldManager/DynamicFormFieldManager"
import FieldTypes from "../Types/FieldTypes"
import UserReferenceTypes from "src/store/UserReference/types"
import schemaBoiler from "../Utils/SchemaBoiler"
import DynamicFormFieldConfig from "./DynamicFormFieldConfig/DynamicFormFieldConfig"
import stateMutator from "../Utils/StateMutator"
import dropDownOptions from "../Utils/DropDownOptions"
import DynamicFormTypes from "../Types/DynamicFormsTypes"
import DynamicFormPaletteTypes from "src/store/DyanmicFormPalette/types"
import buildTemplate from "../Utils/TemplateBuilder/BuildTemplate"
import saveSetUp from "../Utils/SaveSetup"
import axios from "axios"
import AxiosConfig from "src/utilities/AxiosConfig"
import formBoiler from "../Utils/FormBoiler"
import DynamicFormEditorHistory from "./DynamicFormEditorHistory/DynamicFormEditorHistory"
import DynamicFormEditorHeader from "./DynamicFormEditorHeader/DynamicFormEditorHeader"
import { Button } from "react-bootstrap"
import EditReduxColor from "../Utils/EditReduxColor/EditReduxColor"
import store from "src/store/store"
import DynamicFormPreview from "../DynamicForm/DynamicFormPreview/DynamicFormPreview"
import snackBar from "src/utilities/snackBar/SnackBar"
import "./DynamicFormEditor.css"
import Naggy from "src/utilities/naggy/Naggy"

interface Props {
    userReference: UserReferenceTypes
    userId: string | number
    form?: DynamicFormTypes | null
    formTypes: Array<{ CustomFormTypeId: number, Type: string }> | []
    manageParentState: Function
    callParentFunction: Function
    hints: boolean
    palette: Array<DynamicFormPaletteTypes>
}

interface State {
    form: DynamicFormTypes
    template: Array<Array<FieldTypes>> | [[]]
    templateHistory: Array<Array<Array<FieldTypes>>> | [[]]
    target: { row: number, col: number, key: string }
    highlightTarget: { row: number, col: number }
    mouseInFieldManager: boolean
    configTarget: { row: number, col: number }
    dropDownReference: Array<{ name: string, values: Array<UserReferenceTypes> }> | null
    forceMobile: boolean
    editBaseDetails: boolean
    preview: boolean
    editPalette: boolean
}

class DynamicFormEditor extends Component<Props, State> {

    originalTemplate: Array<Array<FieldTypes>> | [[]] = [[]]

    state = {
        form: this.props.form ? this.props.form : formBoiler,
        template: [],
        templateHistory: [],
        target: { row: -1, col: -1, key: "" },
        highlightTarget: { row: -1, col: -1 },
        mouseInFieldManager: false,
        configTarget: { row: -1, col: -1 },
        dropDownReference: null,
        forceMobile: false,
        editBaseDetails: false,
        preview: false,
        editPalette: false
    }

    /**
     * If the form prop is not null, then build the template and set the state.
     */
    componentDidMount() {
        store.dispatch({ type: "RESET_DYNAMIC_FORM_PALETTE", payload: null })
        this.getDropDownReference()
        if (this.props.form) {
            const template = buildTemplate(this.props.form)
            this.manageState([{ key: "template", value: template }])
            this.originalTemplate = JSON.parse(JSON.stringify(template))
        }
    }

    /* A function that is called by the child component to call a function in the parent component. */
    callFunction = (functionToCall: string, args: any) => {
        //@ts-ignore
        this[functionToCall](args)
    }

    /* A function that is used to update the state. It is used to prevent the state from being updated in
    an uncontrolled manner. */
    manageState = (keys: Array<{ key: string, value?: any }>, updateHistory?: boolean) => {
        let newKeys = keys
        if (updateHistory) {//need to create a shallow copy of the template array that doesnt lose the validation functions inside the objects
            let clone: Array<Array<Array<FieldTypes>>> | [[]] = this.state.templateHistory.slice()
            clone.push(this.state.template)
            for (let i = 0; i < clone.length; i++) {
                clone[i] = clone[i].slice();
                for (let j = 0; j < clone[i].length; j++) {
                    if (clone[i][j]) {
                        clone[i][j] = clone[i][j].slice()
                    }
                }
            }
            newKeys.push({ key: "templateHistory", value: clone })
        }

        this.setState(stateMutator.mutate(this.state, newKeys))
    }

    handleChange = (event: { target: { value: any } }) => {
        const input = event.target.value
        const target = this.state.target
        let template = this.state.template
        //@ts-ignore
        template[target.row][target.col][target.key] = input === "" ? null : input
        this.manageState([{ key: "template", value: template }])
    }

    /* Adding or removing a row to the template. */
    addOrRemoveRow = (row: number) => {
        let template: Array<Array<FieldTypes>> | [[]] = this.state.template
        if (row < 0) {
            template.push([schemaBoiler])
        } else {
            delete template[row]
        }
        template = template.filter(function (element: any) { return element !== undefined })
        this.manageState([{ key: "template", value: template }], true)
    }

    /* Adding or removing a column to a table. */
    addOrRemoveColumn = (type: string, row: number, col: number, purge?: boolean) => {
        let field = JSON.parse(JSON.stringify(schemaBoiler))
        let template: Array<Array<FieldTypes>> | [[]] = this.state.template
        if (!purge) {
            //@ts-ignore
            field.Type = type
            field.Name = type
            template[row][col] = field
            template[row][col].CustomValues = []
        } else {
            const index = template[row].indexOf(template[row][col])
            if (index > -1) {
                template[row].splice(index, 1)
            }
        }
        this.manageState([{ key: "template", value: template }], true)
    }

    /* Getting the drop down reference from the user reference. */
    getDropDownReference = () => {
        const reference = this.props.userReference
        if (reference) {
            this.manageState([{ key: "dropDownReference", value: dropDownOptions.getReference(reference) }])
        } else {
            setTimeout(() => {
                this.getDropDownReference()
            }, 500)
        }
    }

    saveForm = () => {
        const form = saveSetUp.setUpSave(this.state.form, this.state.template, this.props.userId, this.props.palette, true)
        // console.log("df_DynamicFormEditor saveForm()", form)
        axios.post(process.env.REACT_APP_API_URL + "/api/company/AddEditDynamicForm", form, AxiosConfig.configValue("min"))
            .then(() => {
                this.props.manageParentState([{ key: "formToEdit", value: null }])
                this.props.callParentFunction("getDynamicForms", false, true)
            }).catch((err: Error) => {
                //console.log("df_ Dyanamic forms err", err)
                snackBar({ text: "Error saving form", type: "error", timeout: 3000 })
            })
    }

    render() {

        const props = this.props
        const state = this.state
        const template = state.template
        const hideHistory = state.highlightTarget.row > -1 || state.highlightTarget.col > -1

        // console.log("df_DynamicFormEditor props", props)
        // console.log("df_DynamicFormEditor state", state)
        // console.log("df_DynamicFormEditor template", template)
        // console.log("df_DynamicFormEditor history", state.templateHistory)

        return (
            <div>
                <div className="DynamicFormEditorNeedsBiggerScreen">
                    <span className="DynamicFormEditorNeedsBiggerScreenErrEmoji">{`(>_<)`}</span>
                    <p>You need a larger screen to edit a Dynamic form (min 785px * 600px)</p>
                    <Button variant="secondary" onClick={() => this.props.manageParentState([{ key: "formToEdit", value: null }])}>Got it</Button>
                </div>
                <div id="dynamic-form-editor" className="DynamicFormEditor">
                    <DynamicFormEditorHeader
                        manageParentState={this.manageState}
                        manageGrandparentState={props.manageParentState}
                        callGrandParentFunction={props.callParentFunction}
                        editorState={state}
                        formTypes={this.props.formTypes}
                        saveForm={this.saveForm}
                        hints={props.hints}
                        palette={props.palette}
                    />
                    {state.template.length > 0 &&
                        <DynamicFormFieldManager
                            manageParentState={this.manageState}
                            addOrRemoveColumn={this.addOrRemoveColumn}
                            addOrRemoveRow={this.addOrRemoveRow}
                            highlightTarget={state.highlightTarget}
                            mouseInFieldManager={state.mouseInFieldManager}
                            template={template}
                        />}
                    <DynamicFormTemplate
                        template={template}
                        manageParentState={this.manageState}
                        handleChange={this.handleChange}
                        target={state.target}
                        highlightTarget={state.highlightTarget}
                        mouseInFieldManager={state.mouseInFieldManager}
                        addOrRemoveRow={this.addOrRemoveRow}
                        addOrRemoveColumn={this.addOrRemoveColumn}
                        hints={props.hints}
                        forceMobile={state.forceMobile}
                        palette={props.palette}
                    />
                    {state.configTarget.row > -1 && state.configTarget.col > -1 &&
                        <DynamicFormFieldConfig
                            row={state.configTarget.row}
                            col={state.configTarget.col}
                            field={state.template[state.configTarget.row][state.configTarget.col]}
                            handleChange={this.handleChange}
                            manageParentState={this.manageState}
                            dropDownReference={state.dropDownReference}
                            addOrRemoveColumn={this.addOrRemoveColumn}
                            template={template}
                            palette={props.palette}
                            hints={props.hints}
                        />}
                    {state.templateHistory.length > 0 && !hideHistory &&
                        <DynamicFormEditorHistory
                            history={state.templateHistory}
                            manageParentState={this.manageState}
                            originalTemplate={this.originalTemplate}
                            hints={props.hints}
                        />}
                    {state.preview &&
                        <DynamicFormPreview
                            form={saveSetUp.setUpSave(this.state.form, this.state.template, this.props.userId, this.props.palette, true)}
                            manageParentState={this.manageState}
                            palette={props.palette}
                            enableToolbox={false}
                            callbackKey={"preview"}
                        />}
                    {state.editPalette &&
                        <EditReduxColor
                            form={saveSetUp.setUpSave(this.state.form, this.state.template, this.props.userId, this.props.palette, true)}
                            manageParentState={this.manageState}
                            palette={props.palette}
                            template={template}
                            hints={props.hints}
                        />}
                    <Naggy
                        isDirty={state.templateHistory.length > 0}
                        disabledElements={["dynamic-form-editor-header-close"]}
                        message={"Exit without saving?"}
                        exitBtnText={"Exit"}
                        discard={() => this.props.manageParentState([{ key: "formToEdit", value: null }])}
                    />
                </div>
            </div>
        )
    }
}

export default DynamicFormEditor